/**
 * WebRTC Signaling Service
 * 
 * Uses Firestore for WebRTC signaling (offers, answers, ICE candidates)
 * Works with webrtcService for AUDIO-ONLY calls
 */

import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';

export interface SignalingCall {
  id: string;
  callerId: string;
  calleeId: string;
  status: 'offer_created' | 'answer_created' | 'connected' | 'ended';
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
}

const CALLS_COLLECTION = 'webrtcCalls';
const CANDIDATES_SUBCOLLECTION = 'candidates';

/**
 * Create or update a call document with an offer
 * Uses predictable ID (connectionFlow.id) so both sides find the same call
 */
export async function createOrUpdateCallWithOffer(
  callId: string,
  callerId: string,
  calleeId: string,
  offer: RTCSessionDescriptionInit
): Promise<SignalingCall> {
  const ref = doc(db, CALLS_COLLECTION, callId);

  await setDoc(ref, {
    callerId,
    calleeId,
    status: 'offer_created',
    offer,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  console.log('✅ [Signaling] Offer created for call:', callId);

  return {
    id: ref.id,
    callerId,
    calleeId,
    status: 'offer_created',
    offer,
  };
}

/**
 * Set answer on existing call
 */
export async function setAnswerOnCall(
  callId: string,
  answer: RTCSessionDescriptionInit
): Promise<void> {
  const ref = doc(db, CALLS_COLLECTION, callId);
  await updateDoc(ref, {
    status: 'answer_created',
    answer,
    updatedAt: serverTimestamp(),
  });

  console.log('✅ [Signaling] Answer set for call:', callId);
}

/**
 * Listen for answer from remote peer
 */
export function listenForAnswer(
  callId: string,
  onAnswer: (answer: RTCSessionDescriptionInit) => void
): () => void {
  const ref = doc(db, CALLS_COLLECTION, callId);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) return;
    const data = snap.data() as any;
    if (data.status === 'answer_created' && data.answer) {
      console.log('📞 [Signaling] Answer received for call:', callId);
      onAnswer(data.answer as RTCSessionDescriptionInit);
    }
  });
}

/**
 * Get call by ID
 */
export async function getCallById(callId: string): Promise<SignalingCall | null> {
  const ref = doc(db, CALLS_COLLECTION, callId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data() as any;
  return {
    id: snap.id,
    callerId: data.callerId,
    calleeId: data.calleeId,
    status: data.status,
    offer: data.offer,
    answer: data.answer,
  };
}

/**
 * Save an ICE candidate to the call
 */
export async function addIceCandidateToCall(
  callId: string,
  senderId: string,
  candidate: RTCIceCandidateInit
): Promise<void> {
  const callRef = doc(db, CALLS_COLLECTION, callId);
  const candidatesRef = collection(callRef, CANDIDATES_SUBCOLLECTION);
  await setDoc(doc(candidatesRef), {
    senderId,
    candidate,
    createdAt: serverTimestamp(),
  });
}

/**
 * Listen for remote ICE candidates
 */
export function listenForRemoteCandidates(
  callId: string,
  localUserId: string,
  onCandidate: (candidate: RTCIceCandidateInit) => void
): () => void {
  const callRef = doc(db, CALLS_COLLECTION, callId);
  const candidatesRef = collection(callRef, CANDIDATES_SUBCOLLECTION);

  return onSnapshot(candidatesRef, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type !== 'added') return;
      const data = change.doc.data() as any;
      if (!data || !data.candidate) return;
      // Only apply candidates from the other side
      if (data.senderId && data.senderId === localUserId) return;
      onCandidate(data.candidate as RTCIceCandidateInit);
    });
  });
}

/**
 * Clean up call document after call ends
 * IMPORTANT: Call this after webrtcService.endCall() to clean Firestore
 */
export async function cleanupCallDocument(callId: string): Promise<void> {
  try {
    const ref = doc(db, CALLS_COLLECTION, callId);
    
    // Check if document exists before deleting
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      console.log('ℹ️ [Signaling] Call document already deleted:', callId);
      return;
    }
    
    await deleteDoc(ref);
    console.log('✅ [Signaling] Call document cleaned up:', callId);
  } catch (error: any) {
    // Don't throw on permission errors - just log
    if (error?.code === 'permission-denied') {
      console.warn('⚠️ [Signaling] No permission to delete call document (may already be deleted)');
    } else {
      console.error('❌ [Signaling] Error cleaning up call:', error);
    }
  }
}





